import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { animate, motion, useMotionValue, useReducedMotion } from "motion/react";

import {
  clampPagerPanX,
  classifyPagerPanIntent,
  pagerCommitTargetX,
  pagerRestX,
  pointerVelocityX,
  resolveSwipeRelease,
  shouldIgnorePagerPointerTarget,
  type PagerPanIntent,
  type SwipeDirection,
} from "./todayPagerLogic";

const PAGER_SPRING = { type: "spring" as const, stiffness: 420, damping: 40, mass: 0.82 };

type AnimationHandle = { stop: () => void };

export type PeriodPagerNav = {
  goPrev: () => void;
  goNext: () => void;
};

type TodayPeriodPagerProps = {
  canGoPrev: boolean;
  canGoNext: boolean;
  onCommitPrev: () => void;
  onCommitNext: () => void;
  /** Ring viewport order in track: prev | current | next (swipe right → prev). */
  nextPanel: ReactNode;
  currentPanel: ReactNode;
  prevPanel: ReactNode;
  onBindNav?: (nav: PeriodPagerNav) => void;
  /** Bumps when selected period changes so the track recenters without animating. */
  resetKey: string;
  /** Optional wrapper class for the swipe hit target (e.g. ring-sized viewport). */
  className?: string;
  /** When true, native pointer swipe handling is disabled (e.g. ring segment focus). */
  gesturesDisabled?: boolean;
};

export function TodayPeriodPager({
  canGoPrev,
  canGoNext,
  onCommitPrev,
  onCommitNext,
  nextPanel,
  currentPanel,
  prevPanel,
  onBindNav,
  resetKey,
  className,
  gesturesDisabled = false,
}: TodayPeriodPagerProps) {
  const prefersReducedMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const x = useMotionValue(0);
  const commitGenRef = useRef(0);
  const isCommittingRef = useRef(false);
  const activeAnimRef = useRef<AnimationHandle | null>(null);
  const interruptedRef = useRef(false);

  const canGoPrevRef = useRef(canGoPrev);
  const canGoNextRef = useRef(canGoNext);
  const widthRef = useRef(width);
  canGoPrevRef.current = canGoPrev;
  canGoNextRef.current = canGoNext;
  widthRef.current = width;

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const measure = () => setWidth(element.offsetWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const stopActiveAnimation = useCallback(() => {
    interruptedRef.current = true;
    activeAnimRef.current?.stop();
    activeAnimRef.current = null;
    x.stop();
  }, [x]);

  const hardRecenter = useCallback(() => {
    if (width <= 0) return;
    commitGenRef.current += 1;
    isCommittingRef.current = false;
    stopActiveAnimation();
    x.set(pagerRestX(width));
  }, [stopActiveAnimation, width, x]);

  useLayoutEffect(() => {
    hardRecenter();
  }, [resetKey, hardRecenter]);

  const runAnimate = useCallback(
    async (target: number, gen: number): Promise<boolean> => {
      stopActiveAnimation();
      interruptedRef.current = false;
      const controls = animate(x, target, PAGER_SPRING);
      activeAnimRef.current = controls;
      try {
        await controls;
      } finally {
        if (activeAnimRef.current === controls) {
          activeAnimRef.current = null;
        }
      }
      if (interruptedRef.current) return false;
      return gen === commitGenRef.current;
    },
    [stopActiveAnimation, x],
  );

  const snapToRest = useCallback(
    async (animated = true) => {
      const w = widthRef.current;
      if (w <= 0) return;
      const rest = pagerRestX(w);
      if (!animated || prefersReducedMotion) {
        stopActiveAnimation();
        x.set(rest);
        return;
      }
      const gen = commitGenRef.current;
      const completed = await runAnimate(rest, gen);
      if (!completed) {
        x.set(rest);
      }
    },
    [prefersReducedMotion, runAnimate, stopActiveAnimation, x],
  );

  const commitRef = useRef<(direction: SwipeDirection) => void>(() => {});

  const commit = useCallback(
    async (direction: SwipeDirection) => {
      const w = widthRef.current;
      if (w <= 0) return;
      if (direction === "prev" && !canGoPrevRef.current) return;
      if (direction === "next" && !canGoNextRef.current) return;

      if (prefersReducedMotion) {
        if (direction === "prev") onCommitPrev();
        else onCommitNext();
        return;
      }

      if (isCommittingRef.current) return;

      isCommittingRef.current = true;
      const gen = commitGenRef.current;

      try {
        const target = pagerCommitTargetX(w, direction);
        const completed = await runAnimate(target, gen);
        if (!completed || gen !== commitGenRef.current) return;

        if (direction === "prev") onCommitPrev();
        else onCommitNext();

        if (gen !== commitGenRef.current) return;
        stopActiveAnimation();
        x.set(pagerRestX(w));
      } finally {
        if (gen === commitGenRef.current) {
          isCommittingRef.current = false;
        }
      }
    },
    [onCommitNext, onCommitPrev, prefersReducedMotion, runAnimate, stopActiveAnimation, x],
  );

  commitRef.current = (direction) => {
    void commit(direction);
  };

  const goPrev = useCallback(() => {
    commitRef.current("prev");
  }, []);

  const goNext = useCallback(() => {
    commitRef.current("next");
  }, []);

  useEffect(() => {
    onBindNav?.({ goPrev, goNext });
  }, [goNext, goPrev, onBindNav]);

  const snapToRestRef = useRef(snapToRest);
  snapToRestRef.current = snapToRest;

  const stopActiveAnimationRef = useRef(stopActiveAnimation);
  stopActiveAnimationRef.current = stopActiveAnimation;

  const ready = width > 0;
  const containerOpacity = ready || prefersReducedMotion ? 1 : 0;

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !ready || prefersReducedMotion || gesturesDisabled) return;

    let pointerId: number | null = null;
    let startClientX = 0;
    let startClientY = 0;
    let panStartX = 0;
    let startTime = 0;
    let intent: PagerPanIntent = "pending";

    const resetGesture = () => {
      pointerId = null;
      intent = "pending";
    };

    const interruptCommit = () => {
      if (!isCommittingRef.current) return;
      commitGenRef.current += 1;
      isCommittingRef.current = false;
    };

    const onPointerDown = (event: PointerEvent) => {
      if (pointerId !== null) return;
      if (event.button !== 0) return;
      if (shouldIgnorePagerPointerTarget(event.target)) return;

      interruptCommit();
      stopActiveAnimationRef.current();

      pointerId = event.pointerId;
      startClientX = event.clientX;
      startClientY = event.clientY;
      panStartX = x.get();
      startTime = performance.now();
      intent = "pending";
    };

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return;

      const dx = event.clientX - startClientX;
      const dy = event.clientY - startClientY;

      if (intent === "pending") {
        const nextIntent = classifyPagerPanIntent(dx, dy);
        if (nextIntent === "pending") return;

        intent = nextIntent;
        if (intent === "pan") {
          el.setPointerCapture(event.pointerId);
        } else {
          resetGesture();
          return;
        }
      }

      if (intent === "pan") {
        const w = widthRef.current;
        const nextX = clampPagerPanX(
          panStartX + dx,
          w,
          canGoPrevRef.current,
          canGoNextRef.current,
        );
        x.set(nextX);
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return;

      if (intent === "pan") {
        const dx = event.clientX - startClientX;
        const elapsedMs = performance.now() - startTime;
        const velocityX = pointerVelocityX(dx, elapsedMs);
        const decision = resolveSwipeRelease(
          dx,
          velocityX,
          canGoPrevRef.current,
          canGoNextRef.current,
        );

        if (decision === "prev" || decision === "next") {
          commitRef.current(decision);
        } else {
          void snapToRestRef.current(true);
        }

        try {
          el.releasePointerCapture(event.pointerId);
        } catch {
          // Pointer may already be released.
        }
      }

      resetGesture();
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove, { passive: true });
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };
  }, [gesturesDisabled, prefersReducedMotion, ready, x]);

  if (prefersReducedMotion) {
    return currentPanel;
  }

  return (
    <div
      ref={containerRef}
      className={className ? `overflow-hidden ${className}` : "overflow-hidden"}
      style={{ touchAction: "pan-y", opacity: containerOpacity }}
    >
      <motion.div
        className="flex will-change-transform"
        style={{
          x: ready ? x : 0,
          width: ready ? width * 3 : "100%",
        }}
      >
        <div className="shrink-0" style={{ width: ready ? width : "100%" }}>
          {prevPanel}
        </div>
        <div className="shrink-0" style={{ width: ready ? width : "100%" }}>
          {currentPanel}
        </div>
        <div className="shrink-0" style={{ width: ready ? width : "100%" }}>
          {nextPanel}
        </div>
      </motion.div>
    </div>
  );
}
